import { FC, useCallback, useState } from "react"
import { useParams, useLocation } from "react-router-dom"
import EngagementDetailsHeader from "../../common/ui/engagements/details/header"
import EngagementEmailHistory from "../../common/ui/engagements/details/sections/email-history"
import EngagementQuoteHistory from "../../common/ui/engagements/details/sections/quote-history"
import EngagementSummary from "../../common/ui/engagements/details/sections/summary"
import EngagementEvents from "../../common/ui/engagements/details/sections/events"
import EngagementAdvisorSection from "../../common/ui/engagements/details/sections/ai-advisor"
import { ManageEngagementPanel } from "../../common/ui/engagements"
import { If, SubscriptionDetailsSkeleton, Tabs } from "../../common/ui"
import { RequireRole } from "../../common/validation"
import {
  useFetchSubscriptionDetailsQuery,
  useUpdateSubscriptionMutation,
  useFetchLoanOfficerQuery
} from "../../redux/queries"
import { convertSubscriptionDetailsToPayload } from "../../common/utils"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { SubscriptionDetails } from "../../types"

const EngagementDetailsPage: FC<{}> = () => {
  const { id } = useParams()
  const location = useLocation()

  // Get returnUrl from query params
  const searchParams = new URLSearchParams(location.search)
  const returnUrl = searchParams.get("returnUrl")
  const brandName = searchParams.get("brandName")

  const { data, isLoading: isLoadingSubscriptionDetails } =
    useFetchSubscriptionDetailsQuery({ id: id ?? "" }, { skip: !id })

  const { data: loDetails, isLoading: isLoadingLos } = useFetchLoanOfficerQuery(
    data?.loid ?? "",
    {
      skip: !data?.loid
    }
  )

  const [updateSubscription] = useUpdateSubscriptionMutation()

  const handleChangeRateAlerts = (subscription: SubscriptionDetails) => {
    updateSubscription({
      ...convertSubscriptionDetailsToPayload(subscription),
      id: subscription.id,
      active: subscription.active === "1" ? "0" : "1"
    })
  }

  const handleChangeStatus = (
    subscription: SubscriptionDetails,
    id: string
  ) => {
    updateSubscription({
      ...convertSubscriptionDetailsToPayload(subscription),
      id: subscription.id,
      active: id === "active" ? "1" : "0",
      deleted: id === "delete" ? "1" : "0"
    })
  }

  const [tab, setTab] = useState<string>("summary")
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)

  const handleOpenEdit = useCallback(() => setIsEditPanelOpen(true), [])
  const handleCloseEdit = useCallback(() => setIsEditPanelOpen(false), [])

  const isFetching = isLoadingLos || isLoadingSubscriptionDetails

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["engagements"]}>
      <If
        condition={isFetching}
        then={<SubscriptionDetailsSkeleton />}
        elseCondition={!!(data && loDetails)}
        else={
          <>
            <div className="h-full flex flex-col">
              <div className="pt-6 bg-white">
                <div className="px-6">
                  <EngagementDetailsHeader
                    data={data!}
                    loDetails={loDetails!}
                    handleChangeRateAlerts={handleChangeRateAlerts}
                    onChangeStatus={handleChangeStatus}
                    onEdit={handleOpenEdit}
                    returnUrl={
                      returnUrl ? decodeURIComponent(returnUrl) : undefined
                    }
                    brandName={
                      brandName ? decodeURIComponent(brandName) : undefined
                    }
                  />
                </div>
                <Tabs
                  className="px-6"
                  currentTab={tab}
                  tabs={[
                    { id: "summary", label: "Summary" },
                    { id: "quote-history", label: "Quote history" },
                    { id: "email-history", label: "Email history" },
                    { id: "ai-advisor", label: "AI Advisor" },
                    { id: "activity-history", label: "Activity history" }
                  ]}
                  onChange={setTab}
                />
              </div>
              <div className="p-6 flex-1 bg-white">
                {tab === "summary" && data && (
                  <EngagementSummary data={data} loEmail={loDetails?.email} />
                )}
                {tab === "quote-history" && loDetails && data && (
                  <EngagementQuoteHistory data={data} loDetails={loDetails} />
                )}
                {tab === "email-history" && data && (
                  <EngagementEmailHistory data={data} />
                )}
                {tab === "activity-history" && data && (
                  <EngagementEvents data={data} />
                )}
                {tab === "ai-advisor" && data && (
                  <EngagementAdvisorSection
                    subscriptionId={data.id}
                    leadName={`${data.first_name} ${data.last_name}`.trim()}
                  />
                )}
              </div>
            </div>
            <ManageEngagementPanel
              data={data}
              open={isEditPanelOpen}
              onClose={handleCloseEdit}
              nonqmEnabled
            />
          </>
        }
        default={
          <If
            condition={!data}
            then={
              <div className="p-6">
                Could not fetch details of this engagement.
              </div>
            }
            else={
              <div className="p-6">
                No loan officer is found for this engagement.
              </div>
            }
          />
        }
      />
    </RequireRole>
  )
}

export default EngagementDetailsPage
